import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Close,
  DotsVertical,
  Pen,
  QuestionCircle,
} from 'flowbite-react-icons/outline'
import { useEffect, useRef, useState } from 'react'
import { CopyableValue } from '@/components/common/CopyableValue'
import { formatMrnForDisplay, rawMrnValue } from '@/utils/orderFormUtils'
import { useNavigate } from 'react-router-dom'
import type { OrderStep, PatientInfo } from './OrderTypes'
import { OrderStepsRow } from './OrderStepsRow'

export type HeaderMode = 'start' | 'samples' | 'readonly' | 'submit'

interface EditOption {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  onClick: () => void
}

interface CommonProps {
  patient: PatientInfo
  steps: OrderStep[]
  /** Accepted for call-site compatibility; not used for rendering (steps carry their own status). */
  currentStep?: number
  onStepClick?: (stepId: string) => void
  onHelpClick?: () => void
  isLoading?: boolean
  isNavigationDisabled?: boolean
  showHelp?: boolean
  /** When set, shows a control to edit proband demographics (e.g. standalone / non-Epic). */
  onEditPatient?: () => void
  /** Hard-delete the in-progress draft (GEN-3272). Shown in the order actions menu. */
  onDiscardOrder?: () => void
  /**
   * Extra classes for the sticky header container. Standalone pages pass a
   * negative horizontal margin (`-mx-6`) so the header breaks out of the page's
   * content padding and sits edge-to-edge with the full-width nav bar.
   */
  containerClassName?: string
  /**
   * Optional leading control rendered inline at the start of the title row, to
   * the left of the patient name. Used by the R5 side-nav pages to place the
   * narrow-screen "Open order navigation" burger ({@link OrderNavToggle}) next
   * to the patient name instead of orphaned above the content row. The control
   * is responsible for its own `lg:hidden` visibility.
   */
  navToggle?: React.ReactNode
}

export interface SubmitModeProps extends CommonProps {
  mode: 'submit'
  onBack: () => void
  onSubmit: () => void
  submitDisabled: boolean
  isSubmitting: boolean
  /** Explicit Back disabled state; defaults to `isSubmitting` when omitted. */
  backDisabled?: boolean
  /** CTA label when not submitting; defaults to `'Submit'`. */
  submitLabel?: string
}

export interface OtherModeProps extends CommonProps {
  mode: 'start' | 'samples' | 'readonly'
  onSubmit?: () => void
  canSubmit?: boolean
  editOptions?: EditOption[]
  onUpdateOrder?: () => void
  hasChanges?: boolean
  onStartNewOrder?: () => void
}

export type OrderHeaderProps = SubmitModeProps | OtherModeProps

export function OrderHeader(props: OrderHeaderProps) {
  const {
    patient,
    steps,
    onStepClick,
    onHelpClick,
    isLoading = false,
    isNavigationDisabled = false,
    showHelp = true,
    onEditPatient,
    onDiscardOrder,
    containerClassName,
    navToggle,
  } = props

  const navigate = useNavigate()
  const [editDropdownOpen, setEditDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [optionsMenuOpen, setOptionsMenuOpen] = useState(false)
  const optionsMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setEditDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Header options (kebab) menu: outside-click + Escape to dismiss, mirroring
  // OrderInProgress's "Order actions" kebab.
  useEffect(() => {
    if (!optionsMenuOpen) return
    function handleClickOutside(event: MouseEvent) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node)) {
        setOptionsMenuOpen(false)
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOptionsMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [optionsMenuOpen])

  const handleStartNewOrder = () => {
    if (props.mode !== 'submit') {
      if (props.onStartNewOrder) {
        props.onStartNewOrder()
      } else {
        navigate('/order/start')
      }
    }
  }

  // Render-level patient fallbacks — never write placeholder strings into patient fields.
  const displayName = patient.name.trim() || 'Patient'
  const mrnDisplay = rawMrnValue(patient.mrn) ? formatMrnForDisplay(patient.mrn) : 'MRN not available'
  const mrnRaw = rawMrnValue(patient.mrn)
  const displayAge = patient.age.trim() || 'Age unavailable'
  // '—' is not a date — it signals "no DOB recorded" so the slot is never silently blank.
  const displayDob = patient.dob.trim() || '—'

  return (
    <>
      <div
        data-sticky-page-header
        className={`sticky top-0 z-40 flex flex-col gap-4 border-border-base bg-white p-4 shadow-sm sm:p-6 ${containerClassName ?? ''}`}
      >
        <div className="flex flex-row flex-wrap items-center gap-3 md:flex-nowrap md:gap-6">
          <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-6 md:flex-none">
            {navToggle}
            <div className="flex min-w-0 items-start gap-2 sm:items-center sm:gap-3">
              <div className="flex min-w-0 flex-col gap-1">
                <h1 className="truncate text-xl font-semibold leading-7 tracking-tight text-text-heading sm:text-2xl sm:leading-8">
                  {displayName}
                </h1>
                {mrnRaw ? (
                  <CopyableValue
                    copyValue={mrnRaw}
                    ariaLabel="Copy MRN"
                    className="w-fit text-xs font-medium leading-5 text-text-body"
                  >
                    {mrnDisplay}
                  </CopyableValue>
                ) : (
                  <span className="text-xs font-medium leading-5 text-text-body">{mrnDisplay}</span>
                )}
              </div>
            </div>

            <div className="h-10 w-px shrink-0 bg-border-base sm:h-14" />

            <div className="flex shrink-0 flex-col gap-1">
              <span className="text-sm font-bold leading-5 text-text-heading">
                {displayAge}
                {patient.sex ? ` • ${patient.sex}` : ''}
              </span>
              <span className="text-xs font-medium leading-5 text-text-body">
                {displayDob}
                {patient.gestationalAge ? ` • ${patient.gestationalAge}` : ''}
              </span>
            </div>
          </div>

          {/* Inline progress stepper: shares the header row with the age info on
              md+ screens and wraps to its own full-width line below on small
              screens (order-last) so it doesn't push the actions off-row. */}
          {steps.length > 0 && (
            <>
              <div className="hidden h-14 w-px bg-border-base md:block" />
              <div className="order-last w-full min-w-0 md:order-none md:w-auto md:flex-1 md:px-2">
                <OrderStepsRow
                  variant="wizard"
                  steps={steps}
                  onStepClick={onStepClick}
                  isNavigationDisabled={isNavigationDisabled}
                  isLoading={isLoading}
                />
              </div>
              <div className="hidden h-14 w-px bg-border-base md:block" />
            </>
          )}

          <div className="flex shrink-0 items-center justify-end gap-2">
          {(onEditPatient || onDiscardOrder) && (
            <div className="relative shrink-0" ref={optionsMenuRef}>
              <button
                type="button"
                id="order-header-options-trigger"
                aria-label="Order actions"
                aria-haspopup="menu"
                aria-expanded={optionsMenuOpen}
                aria-controls="order-header-options-menu"
                onClick={() => setOptionsMenuOpen((open) => !open)}
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border-base-medium bg-background-secondary-medium px-4 py-2.5 text-sm font-medium text-text-body shadow-xs transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
              >
                <DotsVertical className="h-5 w-5 shrink-0" aria-hidden />
                <span>More</span>
              </button>
              {optionsMenuOpen && (
                <div
                  id="order-header-options-menu"
                  role="menu"
                  aria-labelledby="order-header-options-trigger"
                  className="absolute right-0 top-full z-50 mt-2 min-w-[min(100vw-2rem,16rem)] overflow-hidden rounded-base border border-border-base bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900 sm:min-w-56"
                >
                  {onEditPatient && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setOptionsMenuOpen(false)
                        onEditPatient()
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-body transition-colors hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800"
                    >
                      <Pen className="h-4 w-4 shrink-0" aria-hidden />
                      Edit Demographics
                    </button>
                  )}
                  {onDiscardOrder && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setOptionsMenuOpen(false)
                        onDiscardOrder()
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-error-600 transition-colors hover:bg-error-50 dark:text-error-400"
                    >
                      <Close className="h-4 w-4 shrink-0" aria-hidden />
                      Discard Order
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {showHelp && (
            <button onClick={onHelpClick} className="btn-icon-lg hidden sm:flex" aria-label="Help">
              <QuestionCircle className="h-5 w-5" />
            </button>
          )}

          {props.mode === 'submit' && (
            <>
              <button
                type="button"
                aria-label="Back"
                onClick={props.onBack}
                disabled={props.backDisabled ?? props.isSubmitting}
                className="inline-flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full border border-border-base-medium bg-background-secondary-medium px-3 py-2 text-xs font-medium text-text-body shadow-xs transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 sm:gap-1.5 sm:px-4 sm:py-2.5 sm:text-sm"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (props.isSubmitting) return
                  props.onSubmit()
                }}
                disabled={props.submitDisabled}
                aria-busy={props.isSubmitting}
                className={`inline-flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full px-3 py-2 text-xs font-medium shadow-xs transition-colors sm:gap-1.5 sm:px-4 sm:py-2.5 sm:text-sm ${
                  !(props.submitDisabled || props.isSubmitting)
                    ? 'bg-brand-600 text-white hover:bg-brand-700'
                    : 'cursor-not-allowed border border-border-base-medium bg-background-disabled text-text-fg-disabled'
                }`}
              >
                <span>{props.isSubmitting ? 'Submitting\u2026' : (props.submitLabel?.trim() || 'Submit')}</span>
              </button>
            </>
          )}

          {props.mode === 'start' && (
            <>
              <button
                onClick={props.onSubmit}
                disabled={!props.canSubmit || isLoading}
                className={`inline-flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full px-3 py-2 text-xs font-medium shadow-xs transition-colors sm:gap-1.5 sm:px-4 sm:py-2.5 sm:text-sm ${
                  props.canSubmit && !isLoading
                    ? 'bg-brand-600 text-white hover:bg-brand-700'
                    : 'cursor-not-allowed border border-border-base-medium bg-background-disabled text-text-fg-disabled'
                }`}
              >
                <span className="hidden sm:inline">Continue</span>
                <span className="sm:hidden">Continue</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}

          {props.mode === 'samples' && (
            <>
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setEditDropdownOpen(!editDropdownOpen)}
                  className="inline-flex items-center justify-center gap-1 rounded-full border border-border-base-medium bg-background-secondary-medium px-3 py-2 text-xs font-medium text-text-body shadow-xs transition-colors hover:bg-gray-100 sm:gap-1.5 sm:px-4 sm:py-2.5 sm:text-sm"
                >
                  <Pen className="h-4 w-4" />
                  <span className="hidden sm:inline">Edit Order</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${editDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {editDropdownOpen && (
                  <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-border-base bg-white py-1 shadow-lg">
                    {(props.editOptions ?? []).map((option) => {
                      const IconComponent = option.icon
                      return (
                        <button
                          key={option.id}
                          onClick={() => {
                            option.onClick()
                            setEditDropdownOpen(false)
                          }}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-text-body transition-colors hover:bg-gray-50"
                        >
                          <IconComponent className="h-4 w-4 text-text-body" />
                          {option.label}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
              <button
                onClick={props.onUpdateOrder}
                disabled={!props.hasChanges}
                className={`inline-flex items-center justify-center gap-1 rounded-full px-3 py-2 text-xs font-medium shadow-xs transition-colors sm:gap-1.5 sm:px-4 sm:py-2.5 sm:text-sm ${
                  props.hasChanges
                    ? 'bg-brand-600 text-white hover:bg-brand-700'
                    : 'cursor-not-allowed border border-border-base-medium bg-background-disabled text-text-fg-disabled'
                }`}
              >
                <span className="hidden sm:inline">Update Order</span>
                <span className="sm:hidden">Update</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}

          {props.mode === 'readonly' && (
            <button
              onClick={handleStartNewOrder}
              className="inline-flex items-center justify-center gap-1 rounded-full bg-brand-600 px-3 py-2 text-xs font-medium text-white shadow-xs transition-colors hover:bg-brand-700 sm:gap-1.5 sm:px-4 sm:py-2.5 sm:text-sm"
            >
              <span className="hidden sm:inline">Start New Order</span>
              <span className="sm:hidden">New Order</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
          </div>
        </div>
      </div>
    </>
  )
}
